import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

function MesDocumentsPage() {
  const { token } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/documents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data.documents || []);
    } catch (err) {
      console.error('Erreur chargement documents:', err);
      setError('Impossible de charger vos documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    if (!file) {
      setError('Veuillez choisir un fichier');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('document', file);
      if (name.trim()) formData.append('name', name.trim());

      const res = await axios.post('/api/documents', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setMessage(res.data.message || '✅ Document ajouté');
      setFile(null);
      setName('');
      // Réinitialiser l'input file
      const input = document.getElementById('doc-file-input');
      if (input) input.value = '';
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'ajout du document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, docName) => {
    if (!window.confirm(`Supprimer le document "${docName}" ?`)) return;
    try {
      await axios.delete(`/api/documents/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
  };

  const iconFor = (doc) => {
    const n = (doc.name || '').toLowerCase();
    if (doc.resourceType === 'image' || /\.(jpe?g|png|gif|webp)$/.test(n)) return '🖼️';
    if (/\.pdf$/.test(n)) return '📕';
    if (/\.(docx?|txt)$/.test(n)) return '📄';
    if (/\.(xlsx?|csv)$/.test(n)) return '📊';
    if (/\.(pptx?)$/.test(n)) return '📽️';
    return '📎';
  };

  return (
    <div style={{ maxWidth: '900px', margin: '2rem auto', padding: '0 16px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px', padding: '24px', color: 'white', marginBottom: '24px'
      }}>
        <h1 style={{ margin: 0, fontSize: '1.6rem' }}>📁 Mes documents</h1>
        <p style={{ margin: '8px 0 0', opacity: 0.9 }}>
          Stockez vos documents personnels ici pour les retrouver facilement, à tout moment.
        </p>
      </div>

      {message && <div style={{ background: '#e8f5e9', color: '#1b5e20', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px' }}>{message}</div>}
      {error && <div style={{ background: '#fdecea', color: '#b71c1c', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px' }}>{error}</div>}

      {/* Formulaire d'upload */}
      <form onSubmit={handleUpload} style={{
        background: '#fff', border: '1px solid #eee', borderRadius: '14px',
        padding: '20px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.05)'
      }}>
        <h3 style={{ marginTop: 0 }}>➕ Ajouter un document</h3>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Fichier *</label>
          <input
            id="doc-file-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
            onChange={(e) => setFile(e.target.files[0])}
            style={{ width: '100%' }}
          />
          <small style={{ color: '#888' }}>PDF, images, Word, Excel, PowerPoint, texte — max 20 Mo</small>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Nom (optionnel)</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Attestation, Billet de train..."
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ccc' }}
          />
        </div>
        <button
          type="submit"
          disabled={uploading}
          style={{
            background: '#667eea', color: 'white', border: 'none', borderRadius: '10px',
            padding: '10px 22px', fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer'
          }}
        >
          {uploading ? '⏳ Envoi...' : '⬆️ Ajouter'}
        </button>
      </form>

      {/* Liste des documents */}
      <h3>Mes documents ({documents.length})</h3>
      {loading ? (
        <p>⏳ Chargement...</p>
      ) : documents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888', background: '#fafafa', borderRadius: '12px' }}>
          <div style={{ fontSize: '48px' }}>📂</div>
          <p>Aucun document pour le moment. Ajoutez-en un ci-dessus.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {documents.map((doc) => (
            <div key={doc._id} style={{
              display: 'flex', alignItems: 'center', gap: '14px',
              background: '#fff', border: '1px solid #eee', borderRadius: '12px', padding: '14px 16px'
            }}>
              <div style={{ fontSize: '28px' }}>{iconFor(doc)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {new Date(doc.createdAt).toLocaleDateString('fr-FR')} {doc.sizeBytes ? `· ${formatSize(doc.sizeBytes)}` : ''}
                </div>
              </div>
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', background: '#f0f4ff', color: '#3730a3', padding: '8px 14px', borderRadius: '8px', fontWeight: 600 }}
              >
                ⬇️ Ouvrir
              </a>
              <button
                onClick={() => handleDelete(doc._id, doc.name)}
                title="Supprimer"
                style={{ background: '#fdecea', color: '#e74c3c', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MesDocumentsPage;
